import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Home, TrendingUp, Globe, RefreshCw, BookOpen, Building2 } from 'lucide-react';
import useReveal from '@/hooks/useReveal';

const Row = ({ label, value }) => (
  <div className="flex justify-between items-baseline py-3.5 border-b border-[#D0D8DC]/40">
    <span className="text-[#4A6572] text-sm">{label}</span>
    <span className="text-[#0B2F44] font-semibold text-sm tracking-tight">{value}</span>
  </div>
);

export default function ServicesPage() {
  useReveal();
  const { t, lang } = useLanguage();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const validTabs = React.useMemo(() => ['first', 'invest', 'nonres', 'refi', 'commercial'], []);
  const [internalTab, setInternalTab] = useState('first');
  const activeTab = (tabParam && validTabs.includes(tabParam)) ? tabParam : internalTab;
  const setActiveTab = (v) => setInternalTab(v);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  }, [tabParam, validTabs]);

  const tabs = [
    { id: 'first', label: t('services', 'tab1'), icon: Home },
    { id: 'invest', label: t('services', 'tab2'), icon: TrendingUp },
    { id: 'nonres', label: t('services', 'tab3'), icon: Globe },
    { id: 'refi', label: t('services', 'tab4'), icon: RefreshCw },
    { id: 'commercial', label: t('services', 'tab6'), icon: Building2 },
  ];

  return (
    <div data-testid="services-page">
      <section className="bg-[#071E2C] pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.14),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/30 to-transparent" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 text-center relative">
          <div className="gmre-overline justify-center mb-5 gmre-reveal">{lang === 'ar' ? 'ما نقدمه' : 'What We Offer'}</div>
          <h1
            className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-white mb-5 tracking-tight gmre-reveal gmre-reveal-delay-1"
            style={{ fontFamily: displayFont, lineHeight: 1.1, textWrap: 'balance' }}
          >
            {t('services', 'title')}
          </h1>
          <div className="gmre-ornament mb-6 gmre-reveal gmre-reveal-delay-2"><div className="line" /><div className="dot" /><div className="line" /></div>
          <p className="text-white/65 max-w-2xl mx-auto text-base lg:text-lg leading-relaxed gmre-reveal gmre-reveal-delay-3" style={{ fontFamily: bodyFont }}>
            {lang === 'ar' ? 'حلول تمويل عقاري مخصصة لكل حالة' : 'Tailored mortgage solutions for every situation'}
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFB] py-16 lg:py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent h-auto mb-12 gmre-reveal">
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  data-testid={`service-tab-${tab.id}`}
                  className="gmre-glass-light gmre-ios-transition flex items-center gap-2 px-5 py-3 rounded-full text-[#4A6572] data-[state=active]:bg-[#0B2F44] data-[state=active]:text-[#C9A84C] data-[state=active]:border-[#0B2F44] text-sm font-medium tracking-wide"
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* First Home Buyer */}
            <TabsContent value="first" data-testid="service-content-first">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-8 tracking-tight" style={{ fontFamily: displayFont }}>
                  {lang === 'ar' ? 'شراء أول عقار' : 'First Home Buyer'}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <h4 className="text-[10px] font-semibold text-[#A68966] mb-4 tracking-[0.24em] uppercase">{lang === 'ar' ? 'الشروط' : 'Parameters'}</h4>
                    <Row label={lang === 'ar' ? 'الحد الأدنى للراتب (موظف)' : 'Min Income (Employee)'} value="AED 10,000/month" />
                    <Row label={lang === 'ar' ? 'الحد الأدنى للراتب (عمل حر)' : 'Min Income (Self-Employed)'} value="AED 15,000+/month" />
                    <Row label={lang === 'ar' ? 'الدفعة المقدمة' : 'Min Down Payment'} value="20% (15% UAE Nationals)" />
                    <Row label={lang === 'ar' ? 'أقصى نسبة قرض' : 'Maximum LTV'} value="80%" />
                    <Row label={lang === 'ar' ? 'أقصى مدة' : 'Maximum Tenure'} value="25 years" />
                  </div>
                  <div>
                    <h4 className="text-[10px] font-semibold text-[#A68966] mb-4 tracking-[0.24em] uppercase">{lang === 'ar' ? 'أفضل الأسعار' : 'Best Rates'}</h4>
                    <div className="bg-gradient-to-br from-[#0B2F44] to-[#071E2C] rounded-2xl p-7 relative overflow-hidden">
                      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,168,76,0.18),transparent_60%)] pointer-events-none" />
                      <div className="relative">
                        <div className="text-center mb-5">
                          <p className="text-[10px] text-white/50 tracking-[0.22em] uppercase mb-2">{lang === 'ar' ? 'أفضل سعر ثابت' : 'Best Fixed Rate (1-Year)'}</p>
                          <p className="text-5xl font-semibold text-white" style={{ fontFamily: 'var(--font-en-display)' }}>3.75<span className="text-[#C9A84C]">%</span></p>
                          <p className="text-[11px] text-[#C9A84C] mt-1 tracking-[0.14em] uppercase">{lang === 'ar' ? 'حصري من جُليد' : 'Guleed Exclusive'}</p>
                        </div>
                        <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent mb-5" />
                        <div className="text-center">
                          <p className="text-[10px] text-white/50 tracking-[0.22em] uppercase mb-2">{lang === 'ar' ? 'أفضل سعر متغير' : 'Best Variable Rate'}</p>
                          <p className="text-2xl font-semibold text-white" style={{ fontFamily: 'var(--font-en-display)' }}>EIBOR + 0.59%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Property Investment */}
            <TabsContent value="invest" data-testid="service-content-invest">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-8 tracking-tight" style={{ fontFamily: displayFont }}>
                  {lang === 'ar' ? 'عقار استثماري' : 'Property Investment'}
                </h3>
                <div className="space-y-0">
                  <Row label={lang === 'ar' ? 'الدفعة المقدمة' : 'Down Payment'} value="25%+ (2nd property)" />
                  <Row label={lang === 'ar' ? 'أقصى نسبة قرض' : 'Maximum LTV'} value="75%" />
                  <Row label={lang === 'ar' ? 'أفضل سعر ثابت' : 'Best Fixed Rate'} value="4.10% p.a." />
                  <Row label={lang === 'ar' ? 'أقصى مدة' : 'Max Tenure'} value="25 years" />
                  <Row label={lang === 'ar' ? 'الحد الأدنى للعقار' : 'Min Property Value'} value="AED 500,000" />
                </div>
              </div>
            </TabsContent>

            {/* Non-Resident */}
            <TabsContent value="nonres" data-testid="service-content-nonres">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-8 tracking-tight" style={{ fontFamily: displayFont }}>
                  {lang === 'ar' ? 'تمويل غير المقيم' : 'Non-Resident Mortgage'}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-[#0B2F44] mb-4 text-sm">{lang === 'ar' ? 'موظف' : 'Employee'}</h4>
                    <Row label={lang === 'ar' ? 'الحد الأدنى للدخل' : 'Min Monthly Income'} value="AED 15,000" />
                    <Row label={lang === 'ar' ? 'الدفعة المقدمة' : 'Down Payment'} value="Up to 50%" />
                    <Row label={lang === 'ar' ? 'أفضل سعر' : 'Best Fixed Rate'} value="4.19% p.a." />
                    <Row label={lang === 'ar' ? 'السعر المتغير' : 'Variable Rate'} value="EIBOR + 0.84%" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0B2F44] mb-4 text-sm">{lang === 'ar' ? 'عمل حر' : 'Self-Employed'}</h4>
                    <Row label={lang === 'ar' ? 'متوسط الدخل' : 'Avg Monthly Income'} value="AED 25,000" />
                    <Row label={lang === 'ar' ? 'الدفعة المقدمة' : 'Down Payment'} value="Up to 50%" />
                    <Row label={lang === 'ar' ? 'أفضل سعر' : 'Best Fixed Rate'} value="4.19% p.a." />
                    <Row label={lang === 'ar' ? 'أقصى مدة' : 'Max Tenure'} value="25 years" />
                  </div>
                </div>
                <p className="mt-6 text-sm text-[#A68966] font-medium">
                  {lang === 'ar' ? 'العملية كاملة عن بعد — بدون زيارة الإمارات' : 'Full digital / remote process — no UAE visit required'}
                </p>
              </div>
            </TabsContent>

            {/* Refinance */}
            <TabsContent value="refi" data-testid="service-content-refi">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2F44] mb-8 tracking-tight" style={{ fontFamily: displayFont }}>
                  {lang === 'ar' ? 'إعادة التمويل وتحرير رأس المال' : 'Refinance & Equity Release'}
                </h3>
                <Row label={lang === 'ar' ? 'أفضل سعر إعادة التمويل' : 'Best Refinance Rate'} value="3.85% p.a." />
                <Row label={lang === 'ar' ? 'تحرير رأس المال' : 'Equity Release'} value="Up to 80% LTV" />
                <Row label={lang === 'ar' ? 'رسوم الانتقال' : 'Transfer Fees'} value="Some banks waive fees" />
                <Row label={lang === 'ar' ? 'وفر المحتمل' : 'Potential Monthly Savings'} value="AED 500 – 3,000+" />
              </div>
            </TabsContent>

            {/* Commercial */}
            <TabsContent value="commercial" data-testid="service-content-commercial">
              <div className="gmre-glass-light rounded-3xl p-8 lg:p-12 gmre-reveal">
                <h3 className="text-xl font-bold text-[#0B2F44] mb-6" style={{ fontFamily: displayFont }}>
                  {lang === 'ar' ? 'العقار التجاري' : 'Commercial Property'}
                </h3>
                <Row label={lang === 'ar' ? 'نوع العقار' : 'Property Types'} value="Office, Retail, Warehouse" />
                <Row label={lang === 'ar' ? 'الدفعة المقدمة' : 'Down Payment'} value="30%–40%" />
                <Row label={lang === 'ar' ? 'أقصى مدة' : 'Max Tenure'} value="15–20 years" />
                <Row label={lang === 'ar' ? 'الحد الأدنى للقرض' : 'Min Loan Amount'} value="AED 1,000,000" />
                <Row label={lang === 'ar' ? 'أفضل سعر' : 'Best Rate'} value="From 5.25% p.a." />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
